import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { LatLng } from '@fcom/core';
import { isNotBlank } from '@fcom/core/utils';

import { BirthDate, PaxDetailsFormData } from '../interfaces';
import { FinnairPassengerCode } from '../api/models';

export interface UrlHashParameters {
  [id: string]: string;
}

const safeDecode = (value: string, sanitizer: DomSanitizer): string => {
  const sanitized = sanitizer.sanitize(SecurityContext.URL, value);
  return decodeURIComponent(sanitized);
};

const safeString = (value: string): string => {
  return value ? value : '';
};

const safeNumberString = (value: string): string => {
  return value && isNaN(Number(value)) ? '' : value;
};

const safeGender = (value: string): string => {
  return value && (value === 'ms' || value === 'mr') ? value : '';
};

export const safePhonePrefix = (countryCodeParameter: string, areaCodeParameter: string): string => {
  const safeCountryCode = safeString(countryCodeParameter);
  const safeAreaCode = safeNumberString(areaCodeParameter);
  return isNotBlank(safeCountryCode) && isNotBlank(safeAreaCode) ? `${safeCountryCode}|${safeAreaCode}` : '';
};

const parseBirthDateFromUrlHash = (birthDateString: string): BirthDate => {
  if (birthDateString) {
    const splitted = birthDateString.split('-');
    if (splitted.length === 3) {
      return {
        dateOfBirth: safeNumberString(splitted[2]),
        monthOfBirth: safeNumberString(splitted[1]),
        yearOfBirth: safeNumberString(splitted[0]),
      };
    }
  }

  return null;
};

export const hashParametersFromUrl = (sanitizer: DomSanitizer): UrlHashParameters => {
  return safeDecode(window.location.hash, sanitizer)
    .substring(1)
    .split(';')
    .map((keyVal) => keyVal.split('='))
    .reduce((obj, valuesArr) => {
      obj[safeDecode(valuesArr[0], sanitizer)] = safeDecode(valuesArr[1], sanitizer);
      return obj;
    }, {});
};

export const parseGeolocationFromUrlHash = (urlParams: UrlHashParameters): LatLng => {
  const lng = Number(safeNumberString(urlParams['lng']));
  const lat = Number(safeNumberString(urlParams['lat']));

  return lng && lat ? { lng, lat } : undefined;
};

export const PARAM_TO_PAX_KEY = {
  gender: 'gender',
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  phoneCountry: 'phoneCountry',
  phonePrefix: 'phonePrefix',
  phoneNumber: 'phoneNumber',
  frequentFlyerNumber: 'ffnumber',
  birthDate: 'birthDate',
};

export const hasPaxKeys = (urlParams: UrlHashParameters) =>
  Object.keys(PARAM_TO_PAX_KEY)
    .map((k) => PARAM_TO_PAX_KEY[k])
    // eslint-disable-next-line no-prototype-builtins
    .reduce((acc, key) => acc && urlParams.hasOwnProperty(key), true);

export const parsePaxFormDataFromUrlHash = (urlParams: UrlHashParameters): PaxDetailsFormData[] => {
  return [
    {
      id: 'ADULT1',
      travelerId: null,
      type: FinnairPassengerCode.ADT,
      gender: safeGender(urlParams[PARAM_TO_PAX_KEY.gender]),
      firstName: safeString(urlParams[PARAM_TO_PAX_KEY.firstName]),
      lastName: safeString(urlParams[PARAM_TO_PAX_KEY.lastName]),
      email: safeString(urlParams[PARAM_TO_PAX_KEY.email]),
      phone: {
        phonePrefix: safePhonePrefix(urlParams[PARAM_TO_PAX_KEY.phoneCountry], urlParams[PARAM_TO_PAX_KEY.phonePrefix]),
        phoneNumber: safeString(urlParams[PARAM_TO_PAX_KEY.phoneNumber]),
      },
      frequentFlyerCard: {
        cardNumber: safeString(urlParams[PARAM_TO_PAX_KEY.frequentFlyerNumber]),
        companyCode: 'AY',
      },
      birthDate: parseBirthDateFromUrlHash(urlParams[PARAM_TO_PAX_KEY.birthDate]),
    },
  ];
};
